/**
 * TimelineTab — Visual status progression for the entity detail panel
 *
 * Shows the status lifecycle: Created → Assigned → In Progress → Completed
 * with timestamps and actor names derived from the audit trail.
 *
 * Uses the same auditTrail query as AuditTrailTab but focuses on
 * status transition events specifically.
 */

import { useMemo } from 'react';
import {
  Stack,
  Text,
  Group,
  Badge,
  Timeline,
  ThemeIcon,
  Center,
  Loader,
  Alert,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconPlayerPlay,
  IconClock,
  IconCirclePlus,
  IconArchive,
  IconCircleX,
  IconPlayerPause,
} from '@tabler/icons-react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useTenantId } from '@/auth';
import type { AuditTrailEntry, AuditTrailResponse } from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// ============================================================================
// GRAPHQL QUERY (same as AuditTrailTab but we filter client-side for status changes)
// ============================================================================

const AUDIT_TRAIL_QUERY = gql`
  query TimelineAuditTrail($filter: AuditTrailFilter!) {
    getAuditTrail(filter: $filter) {
      entries {
        auditId
        action
        entityId
        entityType
        userId
        userName
        timestamp
        tamperDetected
      }
      totalCount
    }
  }
`;

// ============================================================================
// STATUS CONFIG
// ============================================================================

interface StatusConfig {
  color: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
  label: string;
}

const STATUS_MAP: Record<string, StatusConfig> = {
  DRAFT: { color: 'gray', icon: IconClock, label: 'Draft' },
  PENDING: { color: 'yellow', icon: IconClock, label: 'Pending' },
  ACTIVE: { color: 'green', icon: IconPlayerPlay, label: 'Active' },
  IN_PROGRESS: { color: 'blue', icon: IconPlayerPlay, label: 'In Progress' },
  COMPLETED: { color: 'green', icon: IconCircleCheck, label: 'Completed' },
  ARCHIVED: { color: 'gray', icon: IconArchive, label: 'Archived' },
  FAILED: { color: 'red', icon: IconCircleX, label: 'Failed' },
  PAUSED: { color: 'orange', icon: IconPlayerPause, label: 'Paused' },
};

const ACTION_MAP: Record<string, StatusConfig> = {
  CREATE: { color: 'green', icon: IconCirclePlus, label: 'Created' },
  UPDATE: { color: 'blue', icon: IconPlayerPlay, label: 'Updated' },
  DELETE: { color: 'red', icon: IconCircleX, label: 'Deleted' },
  ARCHIVE: { color: 'gray', icon: IconArchive, label: 'Archived' },
};

// ============================================================================
// HELPERS
// ============================================================================

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function durationBetween(ts1: string, ts2: string): string {
  try {
    const diff = new Date(ts2).getTime() - new Date(ts1).getTime();
    if (diff < 0) return '';
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  } catch {
    return '';
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

interface TimelineTabProps {
  entity: PrimitiveItem | null;
  primitiveType: string;
}

export function TimelineTab({ entity, primitiveType }: TimelineTabProps) {
  const tenantId = useTenantId() ?? '';
  const entityId = entity?.id as string | undefined;

  const { data, loading, error } = useQuery<{ getAuditTrail: AuditTrailResponse }>(AUDIT_TRAIL_QUERY, {
    variables: {
      filter: {
        tenantId,
        entityId,
        entityType: primitiveType.toUpperCase(),
        pageSize: 100,
      },
    },
    skip: !entityId || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const records: AuditTrailEntry[] = data?.getAuditTrail?.entries ?? [];

  // Build timeline events from audit records (sorted chronologically)
  const timelineEvents = useMemo(() => {
    return [...records]
      .filter((r: AuditTrailEntry) => !r.tamperDetected)
      .sort((a: AuditTrailEntry, b: AuditTrailEntry) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
  }, [records]);

  // Current status from entity
  const currentStatus = entity?.status as string | undefined;

  if (!entityId) {
    return (
      <Center py="xl">
        <Text c="dimmed" size="sm">Select an entity to view its timeline.</Text>
      </Center>
    );
  }

  if (loading && timelineEvents.length === 0) {
    return (
      <Center py="xl">
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">Loading timeline...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Timeline Unavailable">
        {error.message}
      </Alert>
    );
  }

  if (timelineEvents.length === 0) {
    // Fallback: show basic timeline from entity fields
    return (
      <Stack gap="md" py="md">
        <Text size="sm" fw={600}>Status Progression</Text>
        <Timeline active={-1} bulletSize={24} lineWidth={2}>
          {entity?.createdAt && (
            <Timeline.Item
              bullet={<IconCirclePlus size={12} />}
              title="Created"
            >
              <Text size="xs" c="dimmed">{formatTimestamp(entity.createdAt as string)}</Text>
              {entity.createdBy && (
                <Text size="xs" c="dimmed">by {entity.createdBy as string}</Text>
              )}
            </Timeline.Item>
          )}
          {currentStatus && (
            <Timeline.Item
              bullet={
                (() => {
                  const cfg = STATUS_MAP[currentStatus];
                  const Icon = cfg?.icon ?? IconClock;
                  return <Icon size={12} />;
                })()
              }
              color={STATUS_MAP[currentStatus]?.color ?? 'gray'}
              title={STATUS_MAP[currentStatus]?.label ?? currentStatus}
            >
              {entity?.updatedAt && (
                <Text size="xs" c="dimmed">{formatTimestamp(entity.updatedAt as string)}</Text>
              )}
            </Timeline.Item>
          )}
        </Timeline>
      </Stack>
    );
  }

  return (
    <Stack gap="md" py="md">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Activity Timeline</Text>
        <Badge size="xs" variant="light">{timelineEvents.length} events</Badge>
      </Group>

      <Timeline active={timelineEvents.length - 1} bulletSize={24} lineWidth={2}>
        {timelineEvents.map((event: AuditTrailEntry, idx: number) => {
          const cfg = ACTION_MAP[event.action] ?? { color: 'gray', icon: IconClock, label: event.action };
          const Icon = cfg.icon;
          const duration = idx > 0
            ? durationBetween(timelineEvents[idx - 1]!.timestamp, event.timestamp)
            : '';

          return (
            <Timeline.Item
              key={event.auditId}
              bullet={<Icon size={12} />}
              color={cfg.color}
              title={
                <Group gap="xs">
                  <Text size="sm" fw={500}>{cfg.label}</Text>
                  {duration && (
                    <Badge size="xs" variant="outline" color="gray">{duration}</Badge>
                  )}
                </Group>
              }
            >
              <Text size="xs" c="dimmed">
                {formatTimestamp(event.timestamp)}
              </Text>
              <Text size="xs" c="dimmed">
                {event.userName || event.userId}
              </Text>
            </Timeline.Item>
          );
        })}
      </Timeline>

      {/* Current status indicator */}
      {currentStatus && (
        <Group gap="xs" mt="sm">
          <Text size="xs" fw={600}>Current:</Text>
          <Badge
            size="sm"
            variant="light"
            color={STATUS_MAP[currentStatus]?.color ?? 'gray'}
          >
            {STATUS_MAP[currentStatus]?.label ?? currentStatus}
          </Badge>
        </Group>
      )}
    </Stack>
  );
}
